import AppModal from "@components/modals/AppModal";
import ThinkingAnimation from "@utils/ui/ThinkingAnimation";
import "@assets/css/mode_tool/translatePointsModal.css";

function AiMusicModal({
  isOpen,
  onClose,
  onGenerate,
  isGenerating,
  pointScope = "all",
  onSelectPointScope,
  error,
  progress = {},
  pointOnly = false,
  languageIso = "en",
  languages = [],
  onSelectLanguage,
  promptSingle = "",
  onPromptSingleChange,
}) {
  const points = Array.isArray(progress.points) ? progress.points : [];
  const total = Number(progress.total) || points.length;
  const completed = Number(progress.completed) || 0;
  const percent = total
    ? Math.min(100, Math.round((completed / total) * 100))
    : 0;
  const counts = points.reduce((result, item) => {
    const status = String(item?.status || "pending");
    result[status] = (result[status] || 0) + 1;
    return result;
  }, {});

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      closeDisabled={isGenerating}
      title="AI Music Background"
      size="sm"
      className="mode-route-translate-modal"
    >
      <p>
        {pointOnly
          ? `Generate instrumental music for this route point in ${String(languageIso).toUpperCase()}, matching its ${String(languageIso).toUpperCase()} voice duration.`
          : `Generate instrumental music for route points in ${String(languageIso).toUpperCase()}, matching each point's ${String(languageIso).toUpperCase()} voice duration.`}
      </p>
      {!progress.completeReceived ? (
        <label className="ai-prompt-edit-label" htmlFor="ai-music-single-prompt">
          Music prompt (optional)
          <textarea
            id="ai-music-single-prompt"
            className="ai-prompt-edit-textarea mode-route-ai-music-prompt"
            value={promptSingle}
            onChange={(event) => onPromptSingleChange?.(event.target.value)}
            placeholder="Add optional instructions for the generated music…"
            rows={4}
            disabled={isGenerating}
          />
        </label>
      ) : null}
      <fieldset
        className="mode-route-translate-languages"
        disabled={isGenerating || progress.completeReceived}
      >
        <legend>Target language</legend>
        {languages.map((language) => {
          const iso = String(language?.iso || "").toLowerCase();
          if (!iso) return null;
          return (
            <label key={iso}>
              <input
                type="radio"
                name="ai-music-language"
                value={iso}
                checked={String(languageIso).toLowerCase() === iso}
                onChange={() => onSelectLanguage?.(iso)}
              />
              <span>{language?.name || iso.toUpperCase()}</span>
              <span className="mode-route-translate-language-iso">
                {iso.toUpperCase()}
              </span>
            </label>
          );
        })}
      </fieldset>
      {!pointOnly && !progress.completeReceived ? (
        <fieldset
          className="mode-route-translate-languages mode-route-ai-point-scope"
          disabled={isGenerating}
        >
          <legend>Points to generate music for</legend>
          {[
            ["all", "All points"],
            ["regular", "Regular points only"],
            ["directions", "Direction points only"],
          ].map(([value, label]) => (
            <label key={value}>
              <input
                type="radio"
                name="ai-music-point-scope"
                value={value}
                checked={pointScope === value}
                onChange={() => onSelectPointScope?.(value)}
              />
              <span>{label}</span>
            </label>
          ))}
        </fieldset>
      ) : null}
      {isGenerating ? (
        <ThinkingAnimation label="Generating route music..." />
      ) : null}
      {points.length ? (
        <section
          className={`mode-route-ai-stream ${isGenerating ? "is-running" : "is-finished"}`}
          aria-live="polite"
        >
          <div className="mode-route-ai-stream-heading">
            <strong>
              {progress.done
                ? "Music generation summary"
                : "Generating route music"}
            </strong>
            <span>
              {completed} / {total} tracks
            </span>
          </div>
          <div
            className="mode-route-ai-stream-track"
            role="progressbar"
            aria-label="AI music generation progress"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={Math.min(completed, total)}
          >
            <span style={{ width: `${percent}%` }} />
          </div>
          <div className="mode-route-ai-stream-counts">
            <span className="is-completed">
              {counts.completed || 0} generated
            </span>
            <span className="is-error">{counts.error || 0} errors</span>
            <span>{counts.pending || 0} waiting</span>
          </div>
          <ol className="mode-route-ai-stream-list">
            {points.map((item, index) => {
              const status = String(item?.status || "pending");
              const labels = {
                completed: "Generated",
                error: "Error",
                pending: "Waiting",
              };
              const icons = { completed: "✓", error: "!", pending: "…" };
              return (
                <li className={`is-${status}`} key={item?.id || index}>
                  <span
                    className="mode-route-ai-stream-status"
                    aria-hidden="true"
                  >
                    {icons[status] || "•"}
                  </span>
                  <span className="mode-route-ai-stream-point">
                    <strong>{item?.label || `Point ${index + 1}`}</strong>
                    {item?.message ? <small>{item.message}</small> : null}
                  </span>
                  <span className="mode-route-ai-stream-label">
                    {labels[status] || status}
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      ) : null}
      {error ? (
        <p className="message-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="tour-add-editor-actions">
        <button
          type="button"
          className="button-ghost"
          onClick={onClose}
          disabled={isGenerating}
        >
          Close
        </button>
        {!progress.completeReceived ? (
          <button
            type="button"
            className="button-primary"
            onClick={onGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : "Generate"}
          </button>
        ) : null}
      </div>
    </AppModal>
  );
}

export default AiMusicModal;
